import archivoCSS from '@fontsource-variable/archivo/wght.css?inline';
import interCSS from '@fontsource-variable/inter/wght.css?inline';
import { ASSETS_URL } from './lib/constants/url.lib';
import { AuthModalStoreProvider } from '@kratos/auth';
import { EditorialsStoreProvider } from './pages/editorials/editorials.store';
import { ErrorScreen } from './components/ErrorScreen';
import { Footer } from './components/shell/Footer';
import { Header } from './components/shell/Header';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  isRouteErrorResponse,
  useRouteError
} from 'react-router';
import { Navbar } from './components/shell/Navbar';
import { PageLoadingIndicator } from './components/PageLoadingIndicator';
import { Script } from './components/Script';
import {
  ShellStoreProvider,
  useShellStore
} from './components/shell/shell.store';
import { Suspense } from 'react';
import { Tracker } from '@atreus/tracker';
import { UIAppProvider, UIHeadElements, UIProvider, useMounted } from '@qcx/ui';
import { algoliaObjectID } from './lib/algolia/utils.lib';
import { dark, light } from './styles/theme';
import { getPrivateEnvVars, getPublicEnvVars } from './lib/env.lib';
import { route } from './route';
import { setupOneSignal, shouldLoadOneSignal } from './lib/onesignal.lib';
import type { Route } from './+types/root';

const theme = { light, dark };

const env = getPublicEnvVars();

const handleHashParams = (hash: string) =>
  new URLSearchParams(hash.replace(/^#\/?/, ''));

export const links: Route.LinksFunction = () => [
  {
    rel: 'icon',
    href: `${ASSETS_URL}/images/qconcursos/favicon.ico`,
    type: 'image/x-icon'
  },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap'
  }
];

export const shouldRevalidate: ShouldRevalidateFunction = () => false;

export function Layout({ children }: { children: React.ReactNode }) {
  useMounted(() => {
    const params = handleHashParams(window.location.hash);

    if (params.has('from_search') && params.get('from_search') !== '') {
      document.addEventListener('click', e =>
        handleClick(e, params.get('from_search') as string)
      );
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  });

  return (
    <ShellStoreProvider>
      <UIAppProvider
        imageConfig={{
          loaderPreset: 'cloudflare-image-resizing',
          loaderOptions: {
            url: 'https://image-resize.qconcursos.com'
          }
        }}
      >
        <html className="light" lang="pt-br">
          <head>
            <meta charSet="utf-8" />
            <meta
              name="viewport"
              content="width=device-width,initial-scale=1,viewport-fit=cover"
            />
            <Meta />
            <style
              dangerouslySetInnerHTML={{
                __html: `${interCSS}${archivoCSS}`
              }}
            />
            <Links />
            <UIHeadElements />
          </head>
          <body>
            <UIProvider theme={theme}>
              <Suspense>
                <PageLoadingIndicator />
              </Suspense>
              <AuthModalStoreProvider>
                <EditorialsStoreProvider>
                  <Suspense>
                    <Header />
                    <Navbar />
                  </Suspense>
                  {children}
                  <Suspense>
                    <Footer />
                  </Suspense>
                </EditorialsStoreProvider>
              </AuthModalStoreProvider>
            </UIProvider>
            <EnvScript env={env} />
            <ScrollRestoration />
            <Scripts />
            <ThirdPartyScripts />
          </body>
        </html>
      </UIAppProvider>
    </ShellStoreProvider>
  );
}

export default function App() {
  return <Outlet />;
}

export const ErrorBoundary = () => {
  const env = getPublicEnvVars();
  const { PUBLIC_APP_VERSION, PUBLIC_APP_ENV } = env;
  const error = useRouteError();
  const isRouteResponseError = isRouteErrorResponse(error);
  const status = isRouteResponseError ? error.status : 500;
  const isServer = typeof window === 'undefined';

  if (env.PUBLIC_APP_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  if (PUBLIC_APP_ENV !== 'development' && isServer && !isRouteResponseError) {
    import('rollbar').then(mod => {
      const { PRIVATE_ROLLBAR_ACCESS_TOKEN } = getPrivateEnvVars();
      const Rollbar = mod.default;
      const rollbar = new Rollbar({
        accessToken: PRIVATE_ROLLBAR_ACCESS_TOKEN,
        captureUncaught: true,
        captureUnhandledRejections: true,
        environment: PUBLIC_APP_ENV,
        payload: {
          environment: PUBLIC_APP_ENV,
          client: {
            javascript: {
              source_map_enabled: true,
              code_version: PUBLIC_APP_VERSION,
              guess_uncaught_frames: true
            }
          }
        }
      });

      rollbar.error(error as Error);
    });
  }

  return getErrorComponent(status);
};

const EnvScript = ({ env }: { env: PublicEnvVars }) => {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `self.ENV = ${JSON.stringify(env)}`
      }}
    />
  );
};

const ThirdPartyScripts = () => {
  return (
    <>
      <ZarazScript />
      <OneSignalScripts />
    </>
  );
};

const OneSignalScripts = () => {
  const env = getPublicEnvVars();

  // Hook deve ser chamado antes de qualquer early return
  useMounted(() => {
    if (shouldLoadOneSignal(env)) {
      setupOneSignal(env);
    }
  });

  // Usa a função centralizada em vez de lógica duplicada
  if (!shouldLoadOneSignal(env)) {
    return null;
  }

  return (
    <Script
      id="onesignal-sdk"
      src="https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js"
      strategy="afterInteractive"
      defer
    />
  );
};

const ZarazScript = () => {
  const env = getPublicEnvVars();
  const handleZarazLoad = useShellStore(s => s.setZarazFetched);

  useMounted(() => {
    // Ensure zaraz loaded event is always called in development.
    if (env.PUBLIC_APP_ENV === 'development') {
      handleZarazLoad();
    }
  });

  return env.PUBLIC_APP_ENV === 'development' ? null : (
    <Script
      src="/cdn-cgi/zaraz/i.js"
      strategy="lazyOnload"
      onLoad={handleZarazLoad}
    />
  );
};

const getErrorComponent = (status: number) => <ErrorScreen status={status} />;

export const handleClick = async (event: MouseEvent, queryID?: string) => {
  const target = event.target as HTMLElement;

  const clickedText =
    target.textContent ||
    // eslint-disable-next-line unicorn/prefer-dom-node-text-content
    target.innerText ||
    target.getAttribute('aria-label') ||
    null;

  if (clickedText) {
    Tracker?.globalSearchConversion({
      'clicked text': clickedText,
      'request path': window.location.pathname,
      'current search id': queryID ?? ''
    });

    const objectID = await algoliaObjectID(
      window.location.origin + window.location.pathname
    );

    try {
      await fetch(route('api-events'), {
        method: 'POST',
        body: JSON.stringify({
          insightsName: 'convertedObjectIDsAfterSearch',
          ...(window.__userSession && {
            userToken: `user-${window.__userSession.id}`,
            authenticatedUserToken: `user-${window.__userSession.id}`
          }),
          eventName: 'global search conversion',
          index: 'calliope_auto_index',
          objectIDs: [objectID],
          queryID
        })
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
};
